import * as React from 'react'
import { ReactElement, useContext, useMemo } from 'react'
import { ISelection } from '@fluentui/react'
import useAxios from '../../../hooks/useAxios'
import { useDispatch } from 'react-redux'
import useUser from '../../../hooks/useUser'
import { MailDirection, Props } from '../../Email/EmailSearchSelectList'
import { SelectableEmail } from '../../../hooks/email/useProcessEmail'
import { FileDataAction } from '../../../hooks/file/useFileData'
import { makeStyles, shorthands, tokens, Toolbar, ToolbarButton, ToolbarGroup, Tooltip } from '@fluentui/react-components'
import { MailAlertRegular } from '@fluentui/react-icons'
import EmailDirectionMenu from './EmailDirectionMenu'
import ProcessEmailDialog from '../../Dialog/ProcessEmail/ProcessEmailDialog'
import DeleteEmailButton from './DeleteEmailButton'
import LinkEmailFolderDialog from '../../Dialog/LinkEmailFolder/LinkEmailFolderDialog'
import { FileAction } from '../../../config/fileConfig'
import IntervalToolbarMenu, { Interval } from '../IntervalToolbarMenu'
import { EmailProcessingAction } from '../../Dialog/ProcessEmail/ProcessEmailForm'
import { useTranslation } from 'react-i18next'
import AuthorizationContext from '../../../context/AuthorizationContext'

export const useEmailToolbarStyles = makeStyles({
    toolbar: {
        justifyContent: 'space-between',
        ...shorthands.padding(tokens.spacingHorizontalS, tokens.spacingHorizontalS, tokens.spacingHorizontalS, 0),
    },
    toolbarGroup: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalS, tokens.spacingHorizontalS),
    },
    buttonActive: {
        backgroundColor: 'rgb(223, 246, 221)',
    },
})

export type EmailToolbarProps = Props & {
    interval: Interval
    direction: MailDirection
    setInterval: (interval: Interval) => void
    setDirection: (direction: MailDirection) => void
    selection: ISelection<SelectableEmail>
    reset: FileDataAction
    setAllowedMessageIds: (ids?: string[]) => void
    // Property to prevent the click event inside the dialog to reach elements outside the dialog which in turn cause the dialog to close.
    setPreventDefaultEvent: (value: boolean) => void
}

export default function EmailToolbar(props: EmailToolbarProps): ReactElement {
    const styles = useEmailToolbarStyles()
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const dispatch = useDispatch()
    const { currentMailbox } = useUser()
    const { t: translate } = useTranslation()

    const mailFolderEnabled = useMemo<boolean>(() => props.file?.data?.mailFolder?.enabled, [props.file, props.file?.data?.mailFolder])

    return (
        <Toolbar size="medium" className={styles.toolbar}>
            <ToolbarGroup role="items">
                {props.selection.getSelectedCount() > 0 && (
                    <>
                        <ProcessEmailDialog {...props} emailMailbox={currentHost?.ReplicationMail} action={EmailProcessingAction.Copy} />
                        <ProcessEmailDialog {...props} emailMailbox={currentHost?.ReplicationMail} action={EmailProcessingAction.Move} />
                        <DeleteEmailButton {...props} emailMailbox={currentHost?.ReplicationMail} />
                    </>
                )}
            </ToolbarGroup>
            <ToolbarGroup role="farItems" className={styles.toolbarGroup}>
                <IntervalToolbarMenu {...props} />
                <EmailDirectionMenu {...props} />
                <Tooltip content={translate('email.filing.subscribe')} relationship="description" withArrow>
                    <ToolbarButton
                        icon={<MailAlertRegular />}
                        disabled={props.isLoading}
                        className={mailFolderEnabled ? styles.buttonActive : undefined}
                        onClick={() => {
                            if (props.file.loaded) {
                                props.setIsLoading(true)
                                axios.post(`/email/${currentMailbox}/folder/${props.file.data.reference}/toggle`).then(() =>
                                    dispatch({
                                        type: FileAction.Get,
                                        payload: axios.get(`/file/${props.file.data.id}`),
                                    }),
                                )
                            }
                        }}
                    />
                </Tooltip>
                <LinkEmailFolderDialog {...props} currentReplication={props.file?.data?.mailFolder} />
            </ToolbarGroup>
        </Toolbar>
    )
}
